// Pure, framework-agnostic transforms over the tacho JSON contract — no Vue, no locale.
// Presentation strings (PL labels, tooltips) stay in the component; here lives only the
// structural logic (activity mapping, ISO-week grouping, vehicle sessions, place alignment,
// event severity, preset range math) so it can be reused/ported (e.g. the client front).
import moment from 'moment'
import { formatDateShort, groupThousands, isOpenSentinelDate, localMinuteOfDay } from './format'

export type ActivityKind = 'drive' | 'work' | 'avail' | 'rest'

/** Tachograph activity -> chart-lane CSS kind (single source of truth). */
export function activityKind(activity: string): ActivityKind {
  switch (activity) {
    case 'Drive': return 'drive'
    case 'Available': return 'avail'
    case 'Work': return 'work'
    default: return 'rest'
  }
}

/** Event/fault type -> severity bucket for colouring (locale-free). */
export function eventSeverity(type: string): 'danger' | 'warn' | 'info' {
  const t = String(type ?? '').toLowerCase()
  if (t.includes('speed')) return 'danger'
  if (/power|supply|security|motion|sensor|card|data|conflict|fault|interrupt/.test(t)) return 'warn'
  return 'info'
}

/** Window start (YYYY-MM-DD) for `daysBack` days ending at (and including) `maxDate`. */
export function presetRangeStart(maxDate: string, daysBack: number): string {
  return moment(maxDate, 'YYYY-MM-DD').subtract(daysBack - 1, 'days').format('YYYY-MM-DD')
}

/**
 * CardPlaces belonging to a day, aligned to its local frame. Places carry UTC timestamps,
 * so shift them to the offset the backend serialized this day's intervals with (matches the
 * local-day bucketing and handles DST per day). Adds `_date`/`_minute`/`_time`.
 */
export function alignPlacesToDay(places: any[], day: any): any[] {
  const intervals = day?.intervals ?? []
  if (!intervals.length) return []
  const offset = moment.parseZone(intervals[0].start).utcOffset()
  return (places ?? [])
    .filter((p) => p.Timestamp)
    .map((p) => {
      const local = moment.parseZone(p.Timestamp).utcOffset(offset)
      return { ...p, _date: local.format('YYYY-MM-DD'), _minute: local.hours() * 60 + local.minutes(), _time: local.format('HH:mm') }
    })
    .filter((p) => p._date === day.date)
}

export interface WeekMeta {
  key: number
  week: number
  mondayIso: string
  from: string
  to: string
  totals: { drive: number; work: number; available: number; rest: number }
}

/**
 * Group day records by ISO week, summing activity totals. Input order is preserved
 * (pass newest-first to get newest-first weeks).
 */
export function groupDaysByIsoWeek(days: any[]): { key: number; week: WeekMeta; days: any[] }[] {
  const map = new Map<number, { key: number; week: WeekMeta; days: any[] }>()
  for (const d of days) {
    const m = moment(d.date, 'YYYY-MM-DD')
    const key = m.isoWeekYear() * 100 + m.isoWeek()
    if (!map.has(key)) {
      map.set(key, {
        key,
        week: {
          key,
          week: m.isoWeek(),
          mondayIso: m.clone().startOf('isoWeek').format('YYYY-MM-DD'),
          from: m.clone().startOf('isoWeek').format('DD.MM.YYYY'),
          to: m.clone().endOf('isoWeek').format('DD.MM.YYYY'),
          totals: { drive: 0, work: 0, available: 0, rest: 0 },
        },
        days: [],
      })
    }
    const grp = map.get(key) as { key: number; week: WeekMeta; days: any[] }
    grp.days.push(d)
    for (const k of ['drive', 'work', 'available', 'rest'] as const) {
      grp.week.totals[k] += (d.totals?.[k] || 0)
    }
  }
  return [...map.values()]
}

/**
 * Group CardVehiclesUsed records (daily odometer entries, ring-buffer order) into usage
 * sessions: sort by FirstUse, merge consecutive runs of the same vehicle, build a daily-km
 * sparkline and odometer span. Handles the open-session "still in use" sentinel.
 */
export function vehicleSessions(records: any[]): any[] {
  const sorted = [...(records ?? [])]
    .sort((a, b) => String(a.FirstUse ?? '').localeCompare(String(b.FirstUse ?? '')))
  const groups: any[] = []
  let current: any = null
  for (const v of sorted) {
    const key = `${v.RegistrationNation}|${v.RegistrationNumber}`
    if (!current || current.key !== key) {
      current = { key, vrn: v.RegistrationNumber || '—', country: v.RegistrationNation || '', records: [] }
      groups.push(current)
    }
    current.records.push(v)
  }
  return groups.map((g) => {
    const recs = g.records
    const bars = recs.map((r: any) => {
      const km = (r.OdometerBegin != null && r.OdometerEnd != null && r.OdometerEnd >= r.OdometerBegin) ? r.OdometerEnd - r.OdometerBegin : 0
      return { km, date: formatDateShort(r.FirstUse), h: 0 }
    })
    const maxKm = Math.max(1, ...bars.map((b: any) => b.km))
    bars.forEach((b: any) => { b.h = Math.round((b.km / maxKm) * 100) })
    const begins = recs.map((r: any) => r.OdometerBegin).filter((n: any) => n != null)
    const ends = recs.map((r: any) => r.OdometerEnd ?? r.OdometerBegin).filter((n: any) => n != null)
    const last = recs[recs.length - 1]
    const lastEnd = isOpenSentinelDate(last.LastUse) ? last.FirstUse : (last.LastUse || last.FirstUse)
    return {
      vrn: g.vrn,
      country: g.country,
      dateFrom: formatDateShort(recs[0].FirstUse),
      dateTo: formatDateShort(lastEnd),
      days: recs.length,
      open: recs.some((r: any) => r.OdometerEnd == null || isOpenSentinelDate(r.LastUse)),
      bars,
      odoBegin: begins.length ? groupThousands(Math.min(...begins)) : '—',
      odoEnd: ends.length ? groupThousands(Math.max(...ends)) + ' km' : '—',
      totalKm: groupThousands(bars.reduce((s: number, b: any) => s + b.km, 0)),
    }
  })
}

export interface ChartBar { left: string; width: string; kind: ActivityKind; durationMin: number; startIso: string; endIso: string; activity: string; restKind: string | null }

/**
 * Position intervals within a minute window [winStart, winEnd] (percent left/width), tagged
 * with their lane kind. Locale-free — callers add labels/tooltips. Used by the day chart,
 * the zoom slider overview and (via a full-week window) the combined week chart.
 */
export function barsInWindow(intervals: any[], winStart: number, winEnd: number): ChartBar[] {
  const span = winEnd - winStart
  return (intervals ?? []).flatMap((i) => {
    const startMin = localMinuteOfDay(i.start)
    const endMin = startMin + (i.duration_min || 0)
    const visibleStart = Math.max(startMin, winStart)
    const visibleEnd = Math.min(endMin, winEnd)
    if (visibleEnd <= visibleStart) return []

    return [{
      left: ((visibleStart - winStart) / span) * 100 + '%',
      width: ((visibleEnd - visibleStart) / span) * 100 + '%',
      kind: activityKind(i.activity),
      durationMin: i.duration_min,
      startIso: i.start,
      endIso: i.end,
      activity: i.activity,
      // 561/2006 rest classification computed by the backend (null on non-rest intervals).
      restKind: i.rest_kind ?? null,
    }]
  })
}
